import { Flex } from 'foundations-components/components/Flex';
import { Spacer } from 'foundations-components/components/Spacer';
import { View } from 'foundations-components/components/View';
import styled from 'foundations-theming/utils';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { getRootUrl } from 'pricing-pages-lib/utils/urls';
import { trellisBreakpoint } from 'pricing-pages-ui-components/constants/Breakpoints';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { Redirect } from 'react-router-dom';
import { getPrimaryTracker } from 'usage-tracker-container';
import BannerSection from '../components/banners/BannerSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import HeaderLayout from '../components/layout/HeaderLayout';
import AddonsPickerList from '../components/plan-builder/AddonsPickerList';
import HubPlanPickerListContainer from '../components/plan-builder/HubPlanPickerList/HubPlanPickerListContainer';
import PlanBuilderContainer from '../components/plan-builder/PlanBuilderContainer';
import SummaryColumn from '../components/plan-builder/SummaryColumn';
import { BUNDLE_PAGE } from '../constants/ErrorBoundaryNames';
import { PRICING_AND_PURCHASING_FAILED_STATUS } from '../constants/ErrorCauses';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useCountryCode, useDisplayedCurrencyCode } from '../hooks/useCurrencies';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import useIsEligibleForHsfxDiscount from '../hooks/useIsEligibleForHsfxDiscount';
import { isAppAuthSelector } from '../selectors/AuthSelectors';
import { hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { Metrics } from '../utils/metrics';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import withErrorBoundary from '../v2/components/error-boundary/withErrorBoundary';
import PhoneSupportPlanBuilder from '../v2/components/phone-support/PhoneSupportPlanBuilder';
import { SCREENS } from '../v2/constants/tracking/Screens';
import { CREATE_A_BUNDLE } from '../v2/constants/tracking/TrackingSources';
import { trackPageViewWithLastKnownEventProperties } from '../v2/utils/tracking/trackingEventUtils';
import ErrorPage from './ErrorPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const RightColumnWrapper = styled.div.withConfig({
  displayName: "BundlePage__RightColumnWrapper"
})(({
  theme
}) => {
  return {
    inlineSize: '400px',
    marginBlockStart: theme.space[900],
    marginInlineStart: theme.space[300],
    [trellisBreakpoint.cs]: {
      margin: theme.space[0]
    }
  };
});
const BundlePage = () => {
  useDocumentTitle('bundle');
  const country = useCountryCode();
  const currencyCode = useDisplayedCurrencyCode();
  const eligibleForHsfxDiscount = useIsEligibleForHsfxDiscount();
  const isAppAuth = useSelector(isAppAuthSelector);
  useEffect(() => {
    getPrimaryTracker().setProperties({
      activeCurrencyCode: currencyCode,
      countryTerritory: country,
      defaultedTab: '',
      tab: '',
      screen: SCREENS.BUNDLE,
      trackingSource: CREATE_A_BUNDLE
    });
    trackPageViewWithLastKnownEventProperties({});
    return () => {
      getPrimaryTracker().setProperties({
        trackingSource: undefined
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  if (hasPricingAndPurchasingFailed) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: PRICING_AND_PURCHASING_FAILED_STATUS
    });
  }

  // In-App users are not allowed to access the bundle page and should be redirected to
  // the root page for recommendation engine to handle routing.
  if (isAppAuth) {
    Metrics.counter('bundle-page-redirected', {
      reason: 'in-app'
    }).increment();
    return /*#__PURE__*/_jsx(Redirect, {
      to: getRootUrl()
    });
  }
  const productLines = ['MARKETING', 'SALES', 'SERVICE', 'CMS', 'OPERATIONS', 'COMMERCE'];
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
      name: "BUNDLE_PAGE"
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: 'bundle',
      children: /*#__PURE__*/_jsxs(View, {
        inlineSize: "100%",
        as: "main",
        children: [eligibleForHsfxDiscount ? /*#__PURE__*/_jsx(BannerSection, {
          screen: NavItemEnum.BUNDLE
        }) : /*#__PURE__*/_jsx(Spacer, {
          space: "400"
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
          children: [/*#__PURE__*/_jsx(ProductPageTitle, {
            nonProductIdentifier: NavItemEnum.BUNDLE
          }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
            nonProductIdentifier: NavItemEnum.BUNDLE
          })]
        }), /*#__PURE__*/_jsx(PlanBuilderContainer, {
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              inlineSize: "100%",
              alignItems: "center",
              children: [/*#__PURE__*/_jsx(HubPlanPickerListContainer, {
                productLines: productLines
              }), /*#__PURE__*/_jsx(AddonsPickerList, {
                defaultOpen: true
              })]
            }), /*#__PURE__*/_jsxs(RightColumnWrapper, {
              children: [/*#__PURE__*/_jsx(SummaryColumn, {
                shareLinkAnchor: "",
                hubsSignupProductOverride: "bundle"
              }), /*#__PURE__*/_jsx(PhoneSupportPlanBuilder, {
                phoneSourceId: "bundle",
                screen: SCREENS.BUNDLE,
                apiName: "crm-suite-starter-bundle"
              })]
            })]
          })
        })]
      })
    })]
  });
};
export default withErrorBoundary(BundlePage, {
  errorBoundaryName: BUNDLE_PAGE,
  FallbackComponent: () => /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  })
});